import React, { useState, useEffect, useCallback } from 'react';
import { emailAPI, jobsAPI, databaseAPI, pushAPI } from '../services/api';
import './SendEmail.css';

const SendEmail = ({ user }) => {
  const [formData, setFormData] = useState({
    to: '',
    from: '',
  });
  const [singleTemplateId, setSingleTemplateId] = useState('');
  const [bulkTemplateId, setBulkTemplateId] = useState('');
  const [singleBrandId, setSingleBrandId] = useState('');
  const [bulkBrandId, setBulkBrandId] = useState('');
  const [csvBrandId, setCsvBrandId] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkRecipients, setBulkRecipients] = useState('');
  const [csvMode, setCsvMode] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvTemplateId, setCsvTemplateId] = useState('');
  const [uploadedJobId, setUploadedJobId] = useState(null);
  const [databaseMode, setDatabaseMode] = useState(false);
  const [brands, setBrands] = useState([]);
  const [selectedBrandId, setSelectedBrandId] = useState('');
  const [dbTemplateId, setDbTemplateId] = useState('');
  const [emailCount, setEmailCount] = useState(null);
  const [hasSenderMapping, setHasSenderMapping] = useState(false);
  const [singleSenderConfig, setSingleSenderConfig] = useState({ from: '', fromName: '', replyTo: '' });
  const [bulkSenderConfig, setBulkSenderConfig] = useState({ from: '', fromName: '', replyTo: '' });
  const [csvSenderConfig, setCsvSenderConfig] = useState({ from: '', fromName: '', replyTo: '' });
  const [senderConfig, setSenderConfig] = useState({ from: '', fromName: '', replyTo: '' });
  const [channel, setChannel] = useState('email'); // 'email' or 'push'
  const [pushTitle, setPushTitle] = useState('');
  const [pushBody, setPushBody] = useState('');
  const [pushToken, setPushToken] = useState('');
  const [pushTokens, setPushTokens] = useState('');

  const checkBrandSender = useCallback(async (brandId, mode) => {
    try {
      const response = await databaseAPI.getBrandSender(brandId);
      const { hasMapping, senderConfig: config } = response.data.data;
      
      if (mode === 'database') {
        setHasSenderMapping(hasMapping);
        if (hasMapping && config) {
          setSenderConfig(config);
        } else {
          setSenderConfig({ from: '', fromName: '', replyTo: '' });
        }
      } else if (mode === 'single') {
        if (hasMapping && config) {
          setSingleSenderConfig(config);
          setFormData(prev => ({ ...prev, from: config.from }));
        } else {
          setSingleSenderConfig({ from: '', fromName: '', replyTo: '' });
        }
      } else if (mode === 'bulk') {
        if (hasMapping && config) {
          setBulkSenderConfig(config);
          setFormData(prev => ({ ...prev, from: config.from }));
        } else {
          setBulkSenderConfig({ from: '', fromName: '', replyTo: '' });
        }
      } else if (mode === 'csv') {
        if (hasMapping && config) {
          setCsvSenderConfig(config);
        } else {
          setCsvSenderConfig({ from: '', fromName: '', replyTo: '' });
        }
      }
    } catch (err) {
      console.error('Failed to check brand sender:', err);
      if (mode === 'database') {
        setHasSenderMapping(false);
        setSenderConfig({ from: '', fromName: '', replyTo: '' });
      } else if (mode === 'single') {
        setSingleSenderConfig({ from: '', fromName: '', replyTo: '' });
      } else if (mode === 'bulk') {
        setBulkSenderConfig({ from: '', fromName: '', replyTo: '' });
      } else if (mode === 'csv') {
        setCsvSenderConfig({ from: '', fromName: '', replyTo: '' });
      }
    }
  }, []);

  useEffect(() => {
    fetchBrands();
  }, []);

  useEffect(() => {
    if (selectedBrandId) {
      fetchEmailCount(selectedBrandId);
      checkBrandSender(selectedBrandId, 'database');
    } else {
      setHasSenderMapping(false);
      setSenderConfig({ from: '', fromName: '', replyTo: '' });
    }
  }, [selectedBrandId, checkBrandSender]);

  useEffect(() => {
    if (singleBrandId) {
      checkBrandSender(singleBrandId, 'single');
    } else {
      setSingleSenderConfig({ from: '', fromName: '', replyTo: '' });
    }
  }, [singleBrandId, checkBrandSender]);

  useEffect(() => {
    if (bulkBrandId) {
      checkBrandSender(bulkBrandId, 'bulk');
    } else {
      setBulkSenderConfig({ from: '', fromName: '', replyTo: '' });
    }
  }, [bulkBrandId, checkBrandSender]);

  useEffect(() => {
    if (csvBrandId) {
      checkBrandSender(csvBrandId, 'csv');
    } else {
      setCsvSenderConfig({ from: '', fromName: '', replyTo: '' });
    }
  }, [csvBrandId, checkBrandSender]);

  const fetchBrands = async () => {
    try {
      const response = await databaseAPI.getBrands();
      setBrands(response.data.data || []);
    } catch (err) {
      console.error('Failed to fetch brands:', err);
    }
  };

  const fetchEmailCount = async (brandId) => {
    try {
      const response = await databaseAPI.getEmailCount(brandId);
      setEmailCount(response.data.data.count);
    } catch (err) {
      console.error('Failed to fetch email count:', err);
      setEmailCount(null);
    }
  };

  // Removed handleTemplateSelect - no longer needed

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      // Handle Push Notifications
      if (channel === 'push') {
        if (bulkMode) {
          // Bulk push notifications
          if (!pushTokens || !pushTitle || !pushBody) {
            setMessage({ type: 'error', text: 'Please enter device tokens, title, and body' });
            setLoading(false);
            return;
          }

          const tokens = pushTokens.split('\n').map(t => t.trim()).filter(t => t);
          if (tokens.length === 0) {
            setMessage({ type: 'error', text: 'Please enter at least one device token' });
            setLoading(false);
            return;
          }

          const response = await pushAPI.sendBulkPush({
            recipients: tokens.map(token => ({
              token,
              title: pushTitle,
              body: pushBody
            })),
            title: pushTitle,
            body: pushBody,
            userEmail: user?.email
          });

          setMessage({
            type: 'success',
            text: `Successfully sent ${response.data.data.successCount} push notification(s). ${response.data.data.failureCount} failed.`
          });
        } else {
          // Single push notification
          if (!pushToken || !pushTitle || !pushBody) {
            setMessage({ type: 'error', text: 'Please enter device token, title, and body' });
            setLoading(false);
            return;
          }

          const response = await pushAPI.sendPush({
            token: pushToken,
            title: pushTitle,
            body: pushBody,
            userEmail: user?.email
          });

          if (response.data.success) {
            setMessage({ type: 'success', text: 'Push notification sent successfully!' });
          } else {
            setMessage({ type: 'error', text: response.data.error || 'Failed to send push notification' });
          }
        }
        setLoading(false);
        return;
      }

      // Handle Email (existing logic)
      if (databaseMode && selectedBrandId) {
        // Handle database job creation
        if (!dbTemplateId) {
          setMessage({ type: 'error', text: 'Please enter a SendGrid Template ID' });
          setLoading(false);
          return;
        }

        // Validate sender config for unmapped brands
        if (!hasSenderMapping) {
          if (!senderConfig.from) {
            setMessage({ type: 'error', text: 'Please enter a sender email address' });
            setLoading(false);
            return;
          }
        }

        const response = await databaseAPI.createJob({
          brandId: selectedBrandId,
          templateId: dbTemplateId,
          senderConfig: hasSenderMapping ? null : senderConfig
        });
        
        setUploadedJobId(response.data.data.jobId);
        setMessage({ 
          type: 'success', 
          text: `Database job created successfully! Job ID: ${response.data.data.jobId}. Processing ${response.data.data.totalEmails} emails with staggered sending.` 
        });
      } else if (csvMode && csvFile) {
        // Handle CSV upload
        if (!csvTemplateId) {
          setMessage({ type: 'error', text: 'Please enter a SendGrid Template ID' });
          setLoading(false);
          return;
        }
        if (!csvBrandId) {
          setMessage({ type: 'error', text: 'Please select a brand' });
          setLoading(false);
          return;
        }

        // Get sender config for CSV
        const csvFrom = csvSenderConfig.from || formData.from;
        if (!csvFrom) {
          setMessage({ type: 'error', text: 'Please provide sender email address for this brand' });
          setLoading(false);
          return;
        }

        const formDataObj = new FormData();
        formDataObj.append('csvFile', csvFile);
        formDataObj.append('templateId', csvTemplateId);
        formDataObj.append('brandId', csvBrandId);
        if (csvSenderConfig.from) {
          formDataObj.append('senderConfig', JSON.stringify(csvSenderConfig));
        }

        const response = await jobsAPI.uploadCSV(formDataObj);
        setUploadedJobId(response.data.data.jobId);
        setMessage({ 
          type: 'success', 
          text: `CSV uploaded successfully! Job ID: ${response.data.data.jobId}. Processing started.` 
        });
      } else if (bulkMode && bulkRecipients) {
        if (!bulkTemplateId) {
          setMessage({ type: 'error', text: 'Please enter a SendGrid Template ID' });
          setLoading(false);
          return;
        }
        if (!bulkBrandId) {
          setMessage({ type: 'error', text: 'Please select a brand' });
          setLoading(false);
          return;
        }
        const bulkFrom = bulkSenderConfig.from || formData.from;
        if (!bulkFrom) {
          setMessage({ type: 'error', text: 'Please provide sender email address for this brand' });
          setLoading(false);
          return;
        }
        const recipients = bulkRecipients.split('\n').map(email => email.trim()).filter(email => email);
        const response = await emailAPI.sendBulkEmails({
          recipients,
          templateId: bulkTemplateId,
          from: bulkFrom,
          fromName: bulkSenderConfig.fromName,
          replyTo: bulkSenderConfig.replyTo,
        });
        setMessage({ type: 'success', text: `Successfully sent ${response.data.data.length} emails!` });
      } else {
        if (!singleTemplateId) {
          setMessage({ type: 'error', text: 'Please enter a SendGrid Template ID' });
          setLoading(false);
          return;
        }
        if (!singleBrandId) {
          setMessage({ type: 'error', text: 'Please select a brand' });
          setLoading(false);
          return;
        }
        const singleFrom = singleSenderConfig.from || formData.from;
        if (!singleFrom) {
          setMessage({ type: 'error', text: 'Please provide sender email address for this brand' });
          setLoading(false);
          return;
        }
        await emailAPI.sendEmail({
          to: formData.to,
          templateId: singleTemplateId,
          from: singleFrom,
          fromName: singleSenderConfig.fromName,
          replyTo: singleSenderConfig.replyTo,
        });
        setMessage({ type: 'success', text: 'Email sent successfully!' });
      }
      
      // Reset form (except CSV and Database modes)
      if (channel === 'push') {
        // Reset push notification fields
        setPushToken('');
        setPushTokens('');
        setPushTitle('');
        setPushBody('');
      } else if (!csvMode && !databaseMode) {
        setFormData({
          to: '',
          from: '',
        });
        setBulkRecipients('');
        setSingleTemplateId('');
        setBulkTemplateId('');
        setSingleBrandId('');
        setBulkBrandId('');
        setSingleSenderConfig({ from: '', fromName: '', replyTo: '' });
        setBulkSenderConfig({ from: '', fromName: '', replyTo: '' });
      } else if (csvMode) {
        setCsvFile(null);
        setCsvTemplateId('');
        setCsvBrandId('');
        setCsvSenderConfig({ from: '', fromName: '', replyTo: '' });
      } else if (databaseMode) {
        setSelectedBrandId('');
        setDbTemplateId('');
        setEmailCount(null);
        setHasSenderMapping(false);
        setSenderConfig({ from: '', fromName: '', replyTo: '' });
      }
    } catch (err) {
      if (err.response?.data?.requiresSenderConfig) {
        setMessage({
          type: 'error',
          text: 'Please provide sender configuration for this brand',
        });
      } else {
        setMessage({
          type: 'error',
          text: err.response?.data?.error || 'Failed to process request',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
        setCsvFile(file);
        setMessage({ type: '', text: '' });
      } else {
        setMessage({ type: 'error', text: 'Please select a CSV file' });
        setCsvFile(null);
      }
    }
  };

  return (
    <div className="send-email">
      <h1 className="page-title">Send Notification</h1>

      <div className="card">
        {message.text && (
          <div className={`alert alert-${message.type === 'success' ? 'success' : 'error'}`}>
            {message.text}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Channel Selection */}
          <div className="form-group">
            <label className="form-label">Channel</label>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
              <label>
                <input
                  type="radio"
                  name="channel"
                  value="email"
                  checked={channel === 'email'}
                  onChange={(e) => setChannel(e.target.value)}
                  style={{ marginRight: '0.5rem' }}
                />
                📧 Email
              </label>
              <label>
                <input
                  type="radio"
                  name="channel"
                  value="push"
                  checked={channel === 'push'}
                  onChange={(e) => setChannel(e.target.value)}
                  style={{ marginRight: '0.5rem' }}
                />
                🔔 Push Notification
              </label>
            </div>
          </div>

          {/* Mode Selection */}
          <div className="form-group">
            <label className="form-label">Send Mode</label>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              <label>
                <input
                  type="radio"
                  name="mode"
                  checked={!bulkMode && !csvMode && !databaseMode}
                  onChange={() => {
                    setBulkMode(false);
                    setCsvMode(false);
                    setDatabaseMode(false);
                  }}
                  style={{ marginRight: '0.5rem' }}
                />
                Single Email
              </label>
              <label>
                <input
                  type="radio"
                  name="mode"
                  checked={bulkMode && !csvMode && !databaseMode}
                  onChange={() => {
                    setBulkMode(true);
                    setCsvMode(false);
                    setDatabaseMode(false);
                  }}
                  style={{ marginRight: '0.5rem' }}
                />
                Bulk Email
              </label>
              <label>
                <input
                  type="radio"
                  name="mode"
                  checked={csvMode && !databaseMode}
                  onChange={() => {
                    setBulkMode(false);
                    setCsvMode(true);
                    setDatabaseMode(false);
                  }}
                  style={{ marginRight: '0.5rem' }}
                />
                CSV Upload
              </label>
              <label>
                <input
                  type="radio"
                  name="mode"
                  checked={databaseMode}
                  onChange={() => {
                    setBulkMode(false);
                    setCsvMode(false);
                    setDatabaseMode(true);
                  }}
                  style={{ marginRight: '0.5rem' }}
                />
                Database Query
              </label>
            </div>
          </div>

          {/* Push Notification Fields */}
          {channel === 'push' && (
            <>
              {!bulkMode ? (
                <>
                  <div className="form-group">
                    <label className="form-label">Device Token *</label>
                    <input
                      type="text"
                      className="form-input"
                      value={pushToken}
                      onChange={(e) => setPushToken(e.target.value)}
                      placeholder="Enter FCM device token"
                      required
                    />
                    <small style={{ color: '#666', display: 'block', marginTop: '0.5rem' }}>
                      FCM (Firebase Cloud Messaging) device token
                    </small>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Notification Title *</label>
                    <input
                      type="text"
                      className="form-input"
                      value={pushTitle}
                      onChange={(e) => setPushTitle(e.target.value)}
                      placeholder="Enter notification title"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Notification Body *</label>
                    <textarea
                      className="form-textarea"
                      value={pushBody}
                      onChange={(e) => setPushBody(e.target.value)}
                      placeholder="Enter notification message"
                      rows="4"
                      required
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="form-group">
                    <label className="form-label">Device Tokens * (one per line)</label>
                    <textarea
                      className="form-textarea"
                      value={pushTokens}
                      onChange={(e) => setPushTokens(e.target.value)}
                      placeholder="Enter device tokens, one per line"
                      rows="6"
                      required
                    />
                    <small style={{ color: '#666', display: 'block', marginTop: '0.5rem' }}>
                      Enter FCM device tokens, one per line
                    </small>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Notification Title *</label>
                    <input
                      type="text"
                      className="form-input"
                      value={pushTitle}
                      onChange={(e) => setPushTitle(e.target.value)}
                      placeholder="Enter notification title"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Notification Body *</label>
                    <textarea
                      className="form-textarea"
                      value={pushBody}
                      onChange={(e) => setPushBody(e.target.value)}
                      placeholder="Enter notification message"
                      rows="4"
                      required
                    />
                  </div>
                </>
              )}
            </>
          )}

          {/* Email Fields - Only show when channel is email */}
          {channel === 'email' && (
            <>
          {/* Database Query Mode */}
          {databaseMode ? (
            <>
              <div className="form-group">
                <label className="form-label">Select Brand *</label>
                <select
                  className="form-select"
                  value={selectedBrandId}
                  onChange={(e) => setSelectedBrandId(e.target.value)}
                  required
                >
                  <option value="">Select a brand...</option>
                  {brands.map((brand) => (
                    <option key={brand.id} value={brand.id}>
                      {brand.name} (ID: {brand.id})
                    </option>
                  ))}
                </select>
                {emailCount !== null && selectedBrandId && (
                  <small style={{ color: '#666', display: 'block', marginTop: '0.5rem' }}>
                    Found {emailCount} email(s) for this brand
                  </small>
                )}
              </div>
              <div className="form-group">
                <label className="form-label">SendGrid Template ID *</label>
                <input
                  type="text"
                  className="form-input"
                  value={dbTemplateId}
                  onChange={(e) => setDbTemplateId(e.target.value)}
                  placeholder="d-xxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  required
                />
                <small style={{ color: '#666', display: 'block', marginTop: '0.5rem' }}>
                  Enter your SendGrid Dynamic Template ID (e.g., d-xxxxxxxxxxxxxxxxxxxxxxxxxxxxx)
                </small>
                <small style={{ color: '#666', display: 'block', marginTop: '0.5rem' }}>
                  Emails will be sent with staggered timing to prevent failures (batches of 10 with delays)
                </small>
              </div>

              {/* Sender Configuration - Show for unmapped brands */}
              {!hasSenderMapping && selectedBrandId && (
                <>
                  <div className="form-group" style={{ borderTop: '1px solid #e0e0e0', paddingTop: '1rem', marginTop: '1rem' }}>
                    <label className="form-label" style={{ color: '#d9534f', fontWeight: 'bold' }}>
                      Sender Configuration Required *
                    </label>
                    <small style={{ color: '#666', display: 'block', marginBottom: '1rem' }}>
                      This brand does not have a pre-configured sender. Please provide sender email details.
                    </small>
                  </div>
                  <div className="form-group">
                    <label className="form-label">From Email Address *</label>
                    <input
                      type="email"
                      className="form-input"
                      value={senderConfig.from}
                      onChange={(e) => setSenderConfig({ ...senderConfig, from: e.target.value })}
                      placeholder="sender@example.com"
                      required={!hasSenderMapping}
                    />
                    <small style={{ color: '#666', display: 'block', marginTop: '0.5rem' }}>
                      The email address that will appear as the sender
                    </small>
                  </div>
                  <div className="form-group">
                    <label className="form-label">From Name (Optional)</label>
                    <input
                      type="text"
                      className="form-input"
                      value={senderConfig.fromName}
                      onChange={(e) => setSenderConfig({ ...senderConfig, fromName: e.target.value })}
                      placeholder="Company Name"
                    />
                    <small style={{ color: '#666', display: 'block', marginTop: '0.5rem' }}>
                      Display name for the sender (e.g., "GoSedan Support")
                    </small>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Reply-To Email (Optional)</label>
                    <input
                      type="email"
                      className="form-input"
                      value={senderConfig.replyTo}
                      onChange={(e) => setSenderConfig({ ...senderConfig, replyTo: e.target.value })}
                      placeholder="reply@example.com"
                    />
                    <small style={{ color: '#666', display: 'block', marginTop: '0.5rem' }}>
                      Email address for replies (defaults to From email if not provided)
                    </small>
                  </div>
                </>
              )}

              {/* Show mapped sender info for mapped brands */}
              {hasSenderMapping && selectedBrandId && senderConfig.from && (
                <div className="form-group" style={{ background: '#d4edda', padding: '1rem', borderRadius: '4px', border: '1px solid #c3e6cb' }}>
                  <label className="form-label" style={{ color: '#155724', fontWeight: 'bold' }}>
                    ✓ Pre-configured Sender
                  </label>
                  <div style={{ marginTop: '0.5rem', color: '#155724' }}>
                    <div><strong>From:</strong> {senderConfig.fromName ? `${senderConfig.fromName} <${senderConfig.from}>` : senderConfig.from}</div>
                    {senderConfig.replyTo && <div style={{ marginTop: '0.25rem' }}><strong>Reply-To:</strong> {senderConfig.replyTo}</div>}
                  </div>
                </div>
              )}

              {uploadedJobId && (
                <div className="form-group">
                  <div className="alert alert-info">
                    Job ID: {uploadedJobId}. Check the Jobs page to monitor progress.
                  </div>
                </div>
              )}
            </>
          ) : csvMode ? (
            <>
              <div className="form-group">
                <label className="form-label">Select Brand *</label>
                <select
                  className="form-select"
                  value={csvBrandId}
                  onChange={(e) => setCsvBrandId(e.target.value)}
                  required
                >
                  <option value="">Select a brand...</option>
                  {brands.map((brand) => (
                    <option key={brand.id} value={brand.id}>
                      {brand.name} (ID: {brand.id})
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">SendGrid Template ID *</label>
                <input
                  type="text"
                  className="form-input"
                  value={csvTemplateId}
                  onChange={(e) => setCsvTemplateId(e.target.value)}
                  placeholder="d-xxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  required
                />
                <small style={{ color: '#666', display: 'block', marginTop: '0.5rem' }}>
                  Enter your SendGrid Dynamic Template ID (e.g., d-xxxxxxxxxxxxxxxxxxxxxxxxxxxxx)
                </small>
                <small style={{ color: '#666', display: 'block', marginTop: '0.5rem' }}>
                  CSV should have an 'email' column. Other columns will be passed as dynamic template data.
                </small>
              </div>
              {/* Sender Configuration for CSV */}
              {csvBrandId && (
                <>
                  {csvSenderConfig.from ? (
                    <div className="form-group" style={{ background: '#d4edda', padding: '1rem', borderRadius: '4px', border: '1px solid #c3e6cb' }}>
                      <label className="form-label" style={{ color: '#155724', fontWeight: 'bold' }}>
                        ✓ Pre-configured Sender
                      </label>
                      <div style={{ marginTop: '0.5rem', color: '#155724' }}>
                        <div><strong>From:</strong> {csvSenderConfig.fromName ? `${csvSenderConfig.fromName} <${csvSenderConfig.from}>` : csvSenderConfig.from}</div>
                        {csvSenderConfig.replyTo && <div style={{ marginTop: '0.25rem' }}><strong>Reply-To:</strong> {csvSenderConfig.replyTo}</div>}
                      </div>
                    </div>
                  ) : (
                    <div className="form-group" style={{ borderTop: '1px solid #e0e0e0', paddingTop: '1rem', marginTop: '1rem' }}>
                      <label className="form-label" style={{ color: '#d9534f', fontWeight: 'bold' }}>
                        Sender Configuration Required *
                      </label>
                      <div className="form-group">
                        <label className="form-label">From Email Address *</label>
                        <input
                          type="email"
                          className="form-input"
                          value={formData.from}
                          onChange={(e) => setFormData({ ...formData, from: e.target.value })}
                          placeholder="sender@example.com"
                          required
                        />
                      </div>
                    </div>
                  )}
                </>
              )}
              <div className="form-group">
                <label className="form-label">Upload CSV File *</label>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileChange}
                  required
                  style={{ 
                    width: '100%', 
                    padding: '0.5rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }}
                />
                {csvFile && (
                  <small style={{ color: '#666', display: 'block', marginTop: '0.5rem' }}>
                    Selected: {csvFile.name} ({(csvFile.size / 1024).toFixed(2)} KB)
                  </small>
                )}
              </div>
              {uploadedJobId && (
                <div className="form-group">
                  <div className="alert alert-info">
                    Job ID: {uploadedJobId}. Check the Jobs page to monitor progress.
                  </div>
                </div>
              )}
            </>
          ) : bulkMode ? (
            <>
              <div className="form-group">
                <label className="form-label">Select Brand *</label>
                <select
                  className="form-select"
                  value={bulkBrandId}
                  onChange={(e) => setBulkBrandId(e.target.value)}
                  required
                >
                  <option value="">Select a brand...</option>
                  {brands.map((brand) => (
                    <option key={brand.id} value={brand.id}>
                      {brand.name} (ID: {brand.id})
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Recipients (one email per line) *</label>
                <textarea
                  className="form-textarea"
                  value={bulkRecipients}
                  onChange={(e) => setBulkRecipients(e.target.value)}
                  placeholder="email1@example.com&#10;email2@example.com&#10;email3@example.com"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">SendGrid Template ID *</label>
                <input
                  type="text"
                  className="form-input"
                  value={bulkTemplateId}
                  onChange={(e) => setBulkTemplateId(e.target.value)}
                  placeholder="d-xxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  required
                />
                <small style={{ color: '#666', display: 'block', marginTop: '0.5rem' }}>
                  Enter your SendGrid Dynamic Template ID (e.g., d-xxxxxxxxxxxxxxxxxxxxxxxxxxxxx)
                </small>
              </div>
              {/* Sender Configuration for Bulk */}
              {bulkBrandId && (
                <>
                  {bulkSenderConfig.from ? (
                    <div className="form-group" style={{ background: '#d4edda', padding: '1rem', borderRadius: '4px', border: '1px solid #c3e6cb' }}>
                      <label className="form-label" style={{ color: '#155724', fontWeight: 'bold' }}>
                        ✓ Pre-configured Sender
                      </label>
                      <div style={{ marginTop: '0.5rem', color: '#155724' }}>
                        <div><strong>From:</strong> {bulkSenderConfig.fromName ? `${bulkSenderConfig.fromName} <${bulkSenderConfig.from}>` : bulkSenderConfig.from}</div>
                        {bulkSenderConfig.replyTo && <div style={{ marginTop: '0.25rem' }}><strong>Reply-To:</strong> {bulkSenderConfig.replyTo}</div>}
                      </div>
                    </div>
                  ) : (
                    <div className="form-group" style={{ borderTop: '1px solid #e0e0e0', paddingTop: '1rem', marginTop: '1rem' }}>
                      <label className="form-label" style={{ color: '#d9534f', fontWeight: 'bold' }}>
                        Sender Configuration Required *
                      </label>
                      <div className="form-group">
                        <label className="form-label">From Email Address *</label>
                        <input
                          type="email"
                          className="form-input"
                          value={formData.from}
                          onChange={(e) => setFormData({ ...formData, from: e.target.value })}
                          placeholder="sender@example.com"
                          required
                        />
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          ) : (
            <>
              <div className="form-group">
                <label className="form-label">Select Brand *</label>
                <select
                  className="form-select"
                  value={singleBrandId}
                  onChange={(e) => setSingleBrandId(e.target.value)}
                  required
                >
                  <option value="">Select a brand...</option>
                  {brands.map((brand) => (
                    <option key={brand.id} value={brand.id}>
                      {brand.name} (ID: {brand.id})
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">To *</label>
                <input
                  type="email"
                  className="form-input"
                  name="to"
                  value={formData.to}
                  onChange={handleChange}
                  required
                  placeholder="recipient@example.com"
                />
              </div>
              <div className="form-group">
                <label className="form-label">SendGrid Template ID *</label>
                <input
                  type="text"
                  className="form-input"
                  value={singleTemplateId}
                  onChange={(e) => setSingleTemplateId(e.target.value)}
                  placeholder="d-xxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  required
                />
                <small style={{ color: '#666', display: 'block', marginTop: '0.5rem' }}>
                  Enter your SendGrid Dynamic Template ID (e.g., d-xxxxxxxxxxxxxxxxxxxxxxxxxxxxx)
                </small>
              </div>
              {/* Sender Configuration for Single */}
              {singleBrandId && (
                <>
                  {singleSenderConfig.from ? (
                    <div className="form-group" style={{ background: '#d4edda', padding: '1rem', borderRadius: '4px', border: '1px solid #c3e6cb' }}>
                      <label className="form-label" style={{ color: '#155724', fontWeight: 'bold' }}>
                        ✓ Pre-configured Sender
                      </label>
                      <div style={{ marginTop: '0.5rem', color: '#155724' }}>
                        <div><strong>From:</strong> {singleSenderConfig.fromName ? `${singleSenderConfig.fromName} <${singleSenderConfig.from}>` : singleSenderConfig.from}</div>
                        {singleSenderConfig.replyTo && <div style={{ marginTop: '0.25rem' }}><strong>Reply-To:</strong> {singleSenderConfig.replyTo}</div>}
                      </div>
                    </div>
                  ) : (
                    <div className="form-group" style={{ borderTop: '1px solid #e0e0e0', paddingTop: '1rem', marginTop: '1rem' }}>
                      <label className="form-label" style={{ color: '#d9534f', fontWeight: 'bold' }}>
                        Sender Configuration Required *
                      </label>
                      <div className="form-group">
                        <label className="form-label">From Email Address *</label>
                        <input
                          type="email"
                          className="form-input"
                          value={formData.from}
                          onChange={(e) => setFormData({ ...formData, from: e.target.value })}
                          placeholder="sender@example.com"
                          required
                        />
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}
            </>
          )}

          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading 
              ? (channel === 'push' 
                  ? (bulkMode ? 'Sending Push Notifications...' : 'Sending Push...')
                  : databaseMode ? 'Creating Job...' : csvMode ? 'Uploading...' : 'Sending...') 
              : channel === 'push'
                ? (bulkMode ? 'Send Bulk Push Notifications' : 'Send Push Notification')
                : databaseMode
                  ? 'Create Database Job & Start Processing'
                  : csvMode 
                    ? 'Upload CSV & Start Processing' 
                    : bulkMode 
                      ? 'Send Bulk Emails' 
                      : 'Send Email'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default SendEmail;

